import { Capacitor } from '@capacitor/core';
import { pushNotificationService } from './pushNotificationService';
import { authService } from './authService';

const SKIP_KEY = 'fw_push_prompt_skipped_at';
const BANNER_DISMISS_KEY = 'fw_push_banner_dismissed_at';
const SHOWN_SESSION_KEY = 'fw_push_prompt_shown_session';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const AVAILABILITY_EVENT = 'fw:availability-responded';
const SHOW_HOW_TO_EVENT = 'fw:show-push-how-to';
const GRANTED_EVENT = 'fw:push-permission-granted';

let lastAvailabilityEventAt = 0;

export function isNativePushPlatform() {
  return Capacitor.isNativePlatform();
}

function readTimestamp(key) {
  const raw = localStorage.getItem(key);
  const value = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(value) ? value : 0;
}

function isCoolingDown(key) {
  const skippedAt = readTimestamp(key);
  if (!skippedAt) {
    return false;
  }
  return Date.now() - skippedAt < COOLDOWN_MS;
}

export function markPushPromptSkipped() {
  localStorage.setItem(SKIP_KEY, String(Date.now()));
  sessionStorage.setItem(SHOWN_SESSION_KEY, 'true');
}

export function markPushPromptShownThisSession() {
  sessionStorage.setItem(SHOWN_SESSION_KEY, 'true');
}

export function wasPushPromptShownThisSession() {
  return sessionStorage.getItem(SHOWN_SESSION_KEY) === 'true';
}

export function clearPushReminderState() {
  localStorage.removeItem(SKIP_KEY);
  localStorage.removeItem(BANNER_DISMISS_KEY);
  sessionStorage.removeItem(SHOWN_SESSION_KEY);
}

export function dismissPushBanner() {
  localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now()));
}

export function isPushBannerDismissed() {
  return isCoolingDown(BANNER_DISMISS_KEY);
}

export function shouldShowPeriodicPushModal() {
  if (!isNativePushPlatform()) {
    return false;
  }
  if (wasPushPromptShownThisSession()) {
    return false;
  }
  return !isCoolingDown(SKIP_KEY);
}

export function shouldShowPushBanner(permissionGranted) {
  if (!isNativePushPlatform() || permissionGranted) {
    return false;
  }
  return !isPushBannerDismissed();
}

export function shouldShowAfterAvailabilityResponse(permissionGranted) {
  if (!isNativePushPlatform() || permissionGranted) {
    return false;
  }
  return !wasPushPromptShownThisSession();
}

export function notifyAvailabilityResponseSaved() {
  const now = Date.now();
  if (now - lastAvailabilityEventAt < 2000) {
    return;
  }
  lastAvailabilityEventAt = now;
  window.dispatchEvent(new CustomEvent(AVAILABILITY_EVENT));
}

export function requestPushHowToModal() {
  window.dispatchEvent(new CustomEvent(SHOW_HOW_TO_EVENT));
}

export function onPushPermissionGranted(handler) {
  window.addEventListener(GRANTED_EVENT, handler);
  return () => window.removeEventListener(GRANTED_EVENT, handler);
}

export function onShowPushHowTo(handler) {
  window.addEventListener(SHOW_HOW_TO_EVENT, handler);
  return () => window.removeEventListener(SHOW_HOW_TO_EVENT, handler);
}

export function onAvailabilityResponded(handler) {
  window.addEventListener(AVAILABILITY_EVENT, handler);
  return () => window.removeEventListener(AVAILABILITY_EVENT, handler);
}

export async function refreshPushPermissionGranted() {
  return pushNotificationService.checkPermissionStatus();
}

export function getPushBannerMessage() {
  if (authService.isParent()) {
    return "Notifications are off. Enable them so you don't miss your child's match, training and team updates.";
  }
  if (authService.isClubSecretary()) {
    return "Notifications are off. Enable them so you don't miss club and team updates.";
  }
  if (authService.isManager()) {
    return "Notifications are off. Enable them so you don't miss player responses, drop-outs and team updates.";
  }
  return "Notifications are off. Enable them so you don't miss match and training alerts.";
}

export function getHowToEnableSteps() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return [
      'Tap Open Settings',
      'On the FirstWhistle screen, tap Notifications',
      'Turn on Allow Notifications',
      'Return here and tap I\'ve enabled them'
    ];
  }
  return [
    'Tap Open Settings',
    'Tap Notifications (or Apps → FirstWhistle → Notifications)',
    'Turn notifications on',
    'Return here and tap I\'ve enabled them'
  ];
}

export function getHowToEnablePath() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return 'Settings → Apps → FirstWhistle → Notifications → Allow Notifications';
  }
  return 'Settings → Apps → FirstWhistle → Notifications';
}
